import type { DecoratedExperienceEvent } from '@/features/events/types';
import { FeedCategory } from '@/features/sources/components/FeedCategory';
import { FeedEmoji } from '@/features/sources/components/FeedEmoji';
import { FeedLabels } from '@/features/sources/components/FeedLabels';
import { useUpdateFeed } from '@/features/sources/hooks/useUpdateFeed';
import { useUpdateSource } from '@/features/sources/hooks/useUpdateSource';
import {
  FeedOriginKind,
  LabelType,
  type ExperienceFeed,
} from '@/types/gql.generated';
import { useToast, Box } from '@/ui';
import { getErrorMessage, type QueryError } from '@/utils/errors';
import { PreviewCard } from './PreviewCard';
import { SectionLabel } from './SectionLabel';

type Props = {
  feed: ExperienceFeed;
  /** The event the preview card stands the source up with; null when it has none. */
  previewEvent: DecoratedExperienceEvent | null;
};

export const SourceDefaults = ({ feed, previewEvent }: Props) => {
  const { updateFeed } = useUpdateFeed();
  const { updateSource } = useUpdateSource();
  const toast = useToast();

  // The Sources panel lists both internal feeds (EXPERIENCE) and external
  // sources (GOOGLE/ICS), which have separate update mutations (matched by
  // feedExperienceId vs sourceId).
  const isSource = feed.kind !== FeedOriginKind.Experience;

  const handleError = (error: QueryError) =>
    toast.error(getErrorMessage(error));

  const handleEmojiChange = (emoji: string | null) => {
    if (isSource) {
      updateSource({ sourceId: feed.id, emoji }, { onError: handleError });
    } else {
      updateFeed({ feedId: feed.id, emoji }, { onError: handleError });
    }
  };

  const handleCategoryChange = (categoryId: string | null) => {
    if (isSource) {
      updateSource({ sourceId: feed.id, categoryId }, { onError: handleError });
    } else {
      updateFeed({ feedId: feed.id, categoryId }, { onError: handleError });
    }
  };

  const handleWhoLabelsChange = (whoLabelIds: string[]) => {
    if (isSource) {
      updateSource(
        { sourceId: feed.id, whoLabelIds },
        { onError: handleError }
      );
    } else {
      updateFeed({ feedId: feed.id, whoLabelIds }, { onError: handleError });
    }
  };

  const handleLabelsChange = (labelIds: string[]) => {
    if (isSource) {
      updateSource({ sourceId: feed.id, labelIds }, { onError: handleError });
    } else {
      updateFeed({ feedId: feed.id, labelIds }, { onError: handleError });
    }
  };

  return (
    <Box
      bg="white"
      borderColor="line.DEFAULT"
      borderRadius="ah-xl"
      borderStyle="solid"
      borderWidth="1px"
      boxShadow="0 2px 12px rgba(60,50,110,0.08)"
      display="grid"
      gridTemplateColumns="1fr 320px"
      mb={8}
      overflow="hidden"
    >
      {/* ── Left: Source Defaults ─────────────────────────────────────────── */}
      <Box p={6}>
        <SectionLabel>Source defaults</SectionLabel>

        {/* Emoji — applies across the feed's events, over any emoji they
            arrived with. Only a per-event override beats it. */}
        <Box mb={4}>
          <FeedEmoji feed={feed} onChange={handleEmojiChange} />
        </Box>

        {/* Category — drives the feed's name and color in the experience */}
        <Box mb={4}>
          <FeedCategory feed={feed} onChange={handleCategoryChange} />
        </Box>

        {/* Assign someone — default who-labels applied to the feed's events */}
        <Box mb={4}>
          <FeedLabels
            feed={feed}
            labelType={LabelType.Who}
            onChange={handleWhoLabelsChange}
          />
        </Box>

        {/* Labels — default labels applied to the feed's events */}
        <FeedLabels
          feed={feed}
          labelType={LabelType.Default}
          onChange={handleLabelsChange}
        />
      </Box>

      {/* ── Right: Preview ────────────────────────────────────────────────── */}
      <PreviewCard feed={feed} previewEvent={previewEvent} />
    </Box>
  );
};
